import { useEventTracking } from "@/composables/eventTracking"

export type EventType = string | symbol

// An event handler can take an optional event argument
// and should not return a value
export type Handler<T = unknown> = (event: T) => void
export type WildcardHandler<T = Record<string, unknown>> = (type: keyof T, event: T[keyof T]) => void

// An array of all currently registered event handlers for a type
export type EventHandlerList<T = unknown> = Array<Handler<T>>
export type WildCardEventHandlerList<T = Record<string, unknown>> = Array<WildcardHandler<T>>

// A map of event types and their corresponding event handlers.
export type EventHandlerMap<Events extends Record<EventType, unknown>> = Map<
	keyof Events | "*",
	EventHandlerList<Events[keyof Events]> | WildCardEventHandlerList<Events>
>

export interface Emitter<Events extends Record<EventType, unknown>> {
	all: EventHandlerMap<Events>

	on<Key extends keyof Events>(type: Key, handler: Handler<Events[Key]>): void
	on(type: "*", handler: WildcardHandler<Events>): void

	off<Key extends keyof Events>(type: Key, handler?: Handler<Events[Key]>): void
	off(type: "*", handler: WildcardHandler<Events>): void

	emit<Key extends keyof Events>(type: Key, event: Events[Key]): void
	emit<Key extends keyof Events>(type: undefined extends Events[Key] ? Key : never): void
}

/**
 * Mitt: Tiny (~200b) functional event emitter / pubsub.
 * @name mitt
 * @returns {Mitt}
 */
export default function mitt<Events extends Record<EventType, unknown>>(all?: EventHandlerMap<Events>): Emitter<Events> {
	type GenericEventHandler = Handler<Events[keyof Events]> | WildcardHandler<Events>
	all = all || new Map()
	const debounceSet = new Set<string>()
	const doNotDebounce = new Set<string>(["IncrementApiCalls", "DecrementApiCalls", "updateBreadcrumb"])

	return {
		/**
		 * A Map of event names to registered handler functions.
		 */
		all,

		/**
		 * Register an event handler for the given type.
		 * @param {string|symbol} type Type of event to listen for, or `'*'` for all events
		 * @param {Function} handler Function to call in response to given event
		 * @memberOf mitt
		 */
		on<Key extends keyof Events>(type: Key, handler: GenericEventHandler) {
			// Get the existing handlers for the event type
			const handlers = all!.get(type) as EventHandlerList<Events[keyof Events]> | undefined

			if (type.toString().indexOf("invoke") > -1) {
				// console.log("adding event " + type.toString())
				// useEventTracking().addEvent(type.toString())
			}

			if (handlers) {
				// Check if the handler already exists
				const existingHandlerIndex = handlers.findIndex((existingHandler) => existingHandler.toString() === handler.toString())

				if (existingHandlerIndex > -1) {
					// Replace the existing handler
					handlers[existingHandlerIndex] = handler as Handler<Events[keyof Events]>
					//console.log(`Handler for event ${type.toString()} replaced.`)
				} else {
					// Add the new handler
					handlers.push(handler as Handler<Events[keyof Events]>)
					//console.log(`Handler for event ${type.toString()} added.`)
				}
			} else {
				// If no handlers exist for this type, create a new list with the handler
				all!.set(type, [handler as Handler<Events[keyof Events]>])
				// console.log(`Handler for event ${type.toString()} added as the first handler.`)
			}
		},

		/**
		 * Remove an event handler for the given type.
		 * If `handler` is omitted, all handlers of the given type are removed.
		 * @param {string|symbol} type Type of event to unregister `handler` from (`'*'` to remove a wildcard handler)
		 * @param {Function} [handler] Handler function to remove
		 * @memberOf mitt
		 */
		off<Key extends keyof Events>(type: Key, handler?: GenericEventHandler) {
			const handlers: Array<GenericEventHandler> | undefined = all!.get(type)
			if (handlers) {
				if (handler) {
					handlers.splice(handlers.indexOf(handler) >>> 0, 1)
				} else {
					all!.set(type, [])
				}
			}
		},

		/**
		 * Invoke all handlers for the given type.
		 * If present, `'*'` handlers are invoked after type-matched handlers.
		 *
		 * Note: Manually firing '*' handlers is not supported.
		 *
		 * @param {string|symbol} type The event type to invoke
		 * @param {Any} [evt] Any value (object is recommended and powerful), passed to each handler
		 * @memberOf mitt
		 */
		emit<Key extends keyof Events>(type: Key, evt?: Events[Key]) {
			//console.log("Emitting event " + type.toString() + " with data: ", evt)
			let handlers = all!.get(type)
			if (handlers) {
				if (debounceSet.has(type.toString())) {
					// console.log("Debouncing event " + type.toString())
					// useEventTracking().logDebouncedEvent(type.toString())
					return
				} else {
					if (type.toString().indexOf("invoke") > -1) {
						// console.log("Emitting event " + type.toString())
						// useEventTracking().logEventCalled(type.toString())
					}
				}

				;(handlers as EventHandlerList<Events[keyof Events]>).slice().map((handler) => {
					if (!doNotDebounce.has(type.toString())) {
						debounceSet.add(type.toString())
						setTimeout(() => {
							debounceSet.delete(type.toString())
						}, 1000)
					}
					handler(evt!)
				})
			}

			handlers = all!.get("*")
			if (handlers) {
				;(handlers as WildCardEventHandlerList<Events>).slice().map((handler) => {
					handler(type, evt!)
				})
			}
		},
	}
}

const EventBus = mitt()

export { EventBus }
